import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Stethoscope, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { clinic } from "../data/mock";
import { getWhatsAppLink } from "../lib/whatsapp";

const conditionLinks = [
  { label: "Fatty Liver Treatment", to: "/fatty-liver-treatment-varanasi" },
  { label: "Liver Cirrhosis Treatment", to: "/liver-cirrhosis-treatment-varanasi" },
  { label: "Hepatitis Treatment", to: "/hepatitis-treatment-varanasi" },
  { label: "Jaundice & Liver Infection", to: "/jaundice-treatment-varanasi" },
];

const navLinks = [
  { label: "Home", to: "/" },
  { label: "About", to: "/gastro-liver-specialist-dr-indresh-dixit" },
  { label: "Best Liver Doctor", to: "/best-liver-doctor-in-varanasi" },
  { label: "Liver Tests", to: "/liver-tests-varanasi" },
  { label: "Symptoms", to: "/liver-disease-symptoms" },
  { label: "Contact", to: "/contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [mobileDropOpen, setMobileDropOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setDropOpen(false);
    setMobileDropOpen(false);
  }, [location.pathname]);

  const isConditionActive = conditionLinks.some((c) => c.to === location.pathname);

  return (
    <>
      <div className="hidden md:block bg-sky-600 text-white text-sm">
        <div className="container-app flex items-center justify-between py-2">
          <span className="flex items-center gap-2"><Stethoscope size={15} /> Top Gastroenterologist &amp; Varanasi's best Liver Specialist Doctor (Hepatologist)</span>
          <a href={`tel:${clinic.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 hover:text-sky-100 transition-colors">
            <Phone size={15} /> {clinic.phone}
          </a>
        </div>
      </div>
      <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur shadow-md" : "bg-white"}`}>
        <nav className="container-app flex items-center justify-between h-[74px]">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 grid place-items-center text-white shadow-lg shadow-sky-500/30">
              <Stethoscope size={22} />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-bold text-slate-900 text-[17px]">{clinic.doctorName}</div>
              <div className="text-[11px] text-sky-600 font-semibold tracking-wide uppercase">Liver &amp; Gastro Specialist</div>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {navLinks.slice(0, 2).map((l) => (
              <Link key={l.to} to={l.to}
                className={`px-4 py-2 rounded-lg text-[15px] font-medium transition-colors ${location.pathname === l.to ? "text-sky-600 bg-sky-50" : "text-slate-600 hover:text-sky-600 hover:bg-sky-50"}`}>
                {l.label}
              </Link>
            ))}

            <div className="relative" onMouseEnter={() => setDropOpen(true)} onMouseLeave={() => setDropOpen(false)}>
              <button className={`flex items-center gap-1 px-4 py-2 rounded-lg text-[15px] font-medium transition-colors ${isConditionActive ? "text-sky-600 bg-sky-50" : "text-slate-600 hover:text-sky-600 hover:bg-sky-50"}`}>
                Conditions <ChevronDown size={16} className={`transition-transform ${dropOpen ? "rotate-180" : ""}`} />
              </button>
              {dropOpen && (
                <div className="absolute top-full left-0 pt-2 w-64">
                  <div className="bg-white rounded-xl shadow-xl border border-slate-100 p-2">
                    {conditionLinks.map((c) => (
                      <Link key={c.to} to={c.to}
                        className={`block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-colors ${location.pathname === c.to ? "text-sky-600 bg-sky-50" : "text-slate-600 hover:text-sky-600 hover:bg-sky-50"}`}>
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {navLinks.slice(2).map((l) => (
              <Link key={l.to} to={l.to}
                className={`px-4 py-2 rounded-lg text-[15px] font-medium transition-colors ${location.pathname === l.to ? "text-sky-600 bg-sky-50" : "text-slate-600 hover:text-sky-600 hover:bg-sky-50"}`}>
                {l.label}
              </Link>
            ))}
          </div>

          <div className="hidden lg:block">
            <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer">
              <Button className="bg-sky-500 hover:bg-sky-600 text-white rounded-full px-6 shadow-lg shadow-sky-500/30">Book Appointment</Button>
            </a>
          </div>

          <button className="lg:hidden text-slate-700" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </nav>

        {open && (
          <div className="lg:hidden border-t border-slate-100 bg-white max-h-[80vh] overflow-y-auto">
            <div className="container-app py-4 flex flex-col gap-1">
              {navLinks.slice(0, 2).map((l) => (
                <Link key={l.to} to={l.to}
                  className={`px-4 py-3 rounded-lg font-medium ${location.pathname === l.to ? "text-sky-600 bg-sky-50" : "text-slate-700"}`}>
                  {l.label}
                </Link>
              ))}
              <button onClick={() => setMobileDropOpen(!mobileDropOpen)}
                className="flex items-center justify-between px-4 py-3 rounded-lg font-medium text-slate-700">
                Conditions <ChevronDown size={18} className={`transition-transform ${mobileDropOpen ? "rotate-180" : ""}`} />
              </button>
              {mobileDropOpen && (
                <div className="pl-4 flex flex-col gap-1">
                  {conditionLinks.map((c) => (
                    <Link key={c.to} to={c.to}
                      className={`px-4 py-2.5 rounded-lg text-sm font-medium ${location.pathname === c.to ? "text-sky-600 bg-sky-50" : "text-slate-600"}`}>
                      {c.label}
                    </Link>
                  ))}
                </div>
              )}
              {navLinks.slice(2).map((l) => (
                <Link key={l.to} to={l.to}
                  className={`px-4 py-3 rounded-lg font-medium ${location.pathname === l.to ? "text-sky-600 bg-sky-50" : "text-slate-700"}`}>
                  {l.label}
                </Link>
              ))}
              <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer" className="mt-2">
                <Button className="w-full bg-sky-500 hover:bg-sky-600 text-white rounded-full">Book Appointment</Button>
              </a>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Navbar;
